<template>
  <CCard class="mt-4">
    <CCardHeader>
      <h5>
        <translate translate-context="email_templates.debug_logs">Debug Email Logs</translate>
      </h5>
    </CCardHeader>
    <CCardBody>
      <!-- Debug Mode Warning -->
      <CAlert v-if="!isDebugModeActive" color="warning" class="mb-3">
        <div class="d-flex align-items-start">
          <CIcon name="cilWarning" size="lg" class="mr-3 flex-shrink-0"/>
          <div>
            <strong>
              <translate translate-context="email_templates.debug_logs">
                Debug Mode is Currently Disabled
              </translate>
            </strong>
            <p class="mb-0 mt-2">
              <translate translate-context="email_templates.debug_logs">
                To start logging new emails, enable Debug Mode in the email template configuration. When debug mode is active, emails will be logged here instead of being sent.
              </translate>
            </p>
          </div>
        </div>
      </CAlert>

      <!-- Action Buttons -->
      <div class="mb-3 d-flex flex-wrap gap-2">
        <CButton
            color="info"
            variant="outline"
            @click="openBulkSendModal"
        >
          <CIcon name="cilEnvelopeClosed" class="mr-2"/>
          <translate translate-context="email_templates.debug_logs">Bulk Send</translate>
        </CButton>
        <CButton
            v-if="selectedCount > 0"
            color="primary"
            @click="replaySelected"
        >
          <CIcon name="cilPaperPlane" class="mr-2"/>
          <translate translate-context="email_templates.debug_logs" :translate-params="{count: selectedCount}">
            Send Selected (%{count})
          </translate>
        </CButton>
        <CButton
            v-if="selectedCount > 0"
            color="danger"
            variant="outline"
            @click="deleteSelected"
        >
          <CIcon name="cilTrash" class="mr-2"/>
          <translate translate-context="email_templates.debug_logs" :translate-params="{count: selectedCount}">
            Delete Selected (%{count})
          </translate>
        </CButton>
        <PButton
            v-if="debugLogs.results.length > 0"
            color="success"
            @click="exportToExcel"
        >
          <CIcon name="cilSpreadsheet" class="mr-2"/>
          <translate translate-context="email_templates.debug_logs">Export</translate>
        </PButton>
        <CButton
            v-if="selectedCount > 0"
            color="secondary"
            variant="outline"
            @click="markAsSent"
        >
          <CIcon name="cilCheckCircle" class="mr-2"/>
          <translate translate-context="email_templates.debug_logs">Mark as Sent</translate>
        </CButton>
      </div>

      <!-- AjaxTable -->
      <AjaxTable
          ref="ajaxTable"
          :fields="logTableFields"
          :data="debugLogs"
          :fetch="fetchLogs"
          table-name="debugEmailLogs"
          :context-key="emailType"
          @row-clicked="handleRowClick"
      >
        <template #selected-header>
          <input
              type="checkbox"
              :checked="allSelected"
              @change="toggleSelectAll"
              @click.stop
          />
        </template>

        <template #selected="{item}">
          <td class="text-center align-middle">
            <input
                type="checkbox"
                :checked="selectedLogs.includes(item.id)"
                @change="toggleLog(item.id)"
                @click.stop
            />
          </td>
        </template>

        <template #recipient_email="{item}">
          <td class="align-middle">
            <strong>{{ item.recipient_email }}</strong>
          </td>
        </template>

        <template #created_at="{item}">
          <td class="align-middle">
            {{ formatDate(item.created_at) }}
          </td>
        </template>

        <template #sent_at="{item}">
          <td class="align-middle">
            <span v-if="item.sent_at">{{ formatDate(item.sent_at) }}</span>
            <span v-else class="text-muted">-</span>
          </td>
        </template>

        <template #status="{item}">
          <td class="align-middle">
            <CBadge :color="getStatusColor(item)">
              {{ getStatusLabel(item) }}
            </CBadge>
          </td>
        </template>
      </AjaxTable>

      <!-- Results Alert -->
      <CAlert v-if="sendResults" :color="sendResults.error_count > 0 ? 'warning' : 'success'" class="mt-3" @close="sendResults = null">
        <div class="d-flex align-items-start">
          <CIcon :name="sendResults.error_count > 0 ? 'cilWarning' : 'cilCheckCircle'" size="lg" class="mr-3 flex-shrink-0"/>
          <div class="flex-grow-1">
            <strong>
              <translate translate-context="email_templates.debug_logs">
                Send Results
              </translate>
            </strong>
            <div class="mt-2">
              <p class="mb-1">
                <translate translate-context="email_templates.debug_logs" :translate-params="{count: sendResults.success_count}">
                  Successfully sent: %{count}
                </translate>
              </p>
              <p class="mb-1">
                <translate translate-context="email_templates.debug_logs" :translate-params="{count: sendResults.error_count}">
                  Failed: %{count}
                </translate>
              </p>
              <p class="mb-0">
                <translate translate-context="email_templates.debug_logs" :translate-params="{count: sendResults.total_processed}">
                  Total processed: %{count}
                </translate>
              </p>
            </div>

            <!-- Error details -->
            <div v-if="sendResults.errors && sendResults.errors.length > 0" class="mt-3">
              <strong>
                <translate translate-context="email_templates.debug_logs">
                  Errors:
                </translate>
              </strong>
              <ul class="mb-0 mt-2">
                <li v-for="error in sendResults.errors" :key="error.log_id || error.email">
                  <span v-if="error.log_id">
                    <translate translate-context="email_templates.debug_logs" :translate-params="{id: error.log_id, error: error.error}">
                      Log #%{id}: %{error}
                    </translate>
                  </span>
                  <span v-else>
                    <translate translate-context="email_templates.debug_logs" :translate-params="{email: error.email, error: error.error}">
                      %{email}: %{error}
                    </translate>
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </CAlert>
    </CCardBody>

    <!-- Modals -->
    <SendSelectedModal
        ref="sendSelectedModal"
        :selected-count="selectedCount"
        @send="handleSendSelected"
    />
    <BulkSendModal
        ref="bulkSendModal"
        @send="handleBulkSend"
    />
  </CCard>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'
import AjaxTable from '@/domain/core/components/AjaxTable.vue'
import PButton from '@/components/PButton.vue'
import SendSelectedModal from './SendSelectedModal.vue'
import BulkSendModal from './BulkSendModal.vue'

export default {
  name: 'DebugLogsSection',

  components: {
    AjaxTable,
    PButton,
    SendSelectedModal,
    BulkSendModal
  },

  props: {
    emailType: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      selectedLogs: [],
      sendResults: null,
      logTableFields: [
        {
          key: 'selected',
          label: '',
          filter: false,
          sorter: false,
          _style: 'width: 40px;'
        },
        {
          key: 'recipient_email',
          label: this.$pgettext('email_templates.debug_logs', 'Recipient'),
          sorter: true
        },
        {
          key: 'created_at',
          label: this.$pgettext('email_templates.debug_logs', 'Created At'),
          sorter: true,
          dateRangeFrom: 'created_at_from',
          dateRangeTo: 'created_at_to'
        },
        {
          key: 'sent_at',
          label: this.$pgettext('email_templates.debug_logs', 'Sent At'),
          sorter: true,
          dateRangeFrom: 'sent_at_from',
          dateRangeTo: 'sent_at_to'
        },
        {
          key: 'status',
          label: this.$pgettext('email_templates.debug_logs', 'Status'),
          sorter: true,
          _style: 'width: 120px;',
          options: [
            {value: '', label: this.$pgettext('email_templates.debug_logs', 'All Status')},
            {value: 'sent', label: this.$pgettext('email_templates.debug_logs', 'Sent')},
            {value: 'not_sent', label: this.$pgettext('email_templates.debug_logs', 'Not Sent')},
            {value: 'failed', label: this.$pgettext('email_templates.debug_logs', 'Failed')}
          ]
        }
      ]
    }
  },

  computed: {
    ...mapGetters('emailTemplates', [
      'debugLogs',
      'template'
    ]),

    selectedCount() {
      return this.selectedLogs.length
    },

    allSelected() {
      return this.selectedLogs.length === this.debugLogs.results.length && this.debugLogs.results.length > 0
    },

    isDebugModeActive() {
      return this.template?.debug_mode && this.template.debug_mode !== 'off'
    }
  },

  methods: {
    ...mapActions('emailTemplates', [
      'fetchDebugLogs',
      'exportDebugLogs',
      'sendToEmails',
      'replaySelectedDebugLogs',
      'deleteSelectedDebugLogs',
      'markDebugLogsAsSent'
    ]),

    async fetchLogs({filters, pagination, sorting}) {
      await this.fetchDebugLogs({
        emailType: this.emailType,
        filters,
        pagination,
        sorting
      })
    },

    getCurrentFilters() {
      return this.$refs.ajaxTable ? this.$refs.ajaxTable.getFilters() : {}
    },

    toggleLog(logId) {
      const index = this.selectedLogs.indexOf(logId)
      if (index > -1) {
        this.selectedLogs.splice(index, 1)
      } else {
        this.selectedLogs.push(logId)
      }
    },

    handleRowClick(item) {
      this.toggleLog(item.id)
    },

    toggleSelectAll() {
      if (this.allSelected) {
        this.selectedLogs = []
      } else {
        this.selectedLogs = this.debugLogs.results.map(log => log.id)
      }
    },

    openBulkSendModal() {
      this.$refs.bulkSendModal.open()
    },

    async handleBulkSend({ emailAddresses, sendOnlyPending }) {
      this.confirm({
        title: this.$pgettext('email_templates.debug_logs', 'Send Bulk Emails'),
        message: this.$pgettext('email_templates.debug_logs', 'Are you sure you want to send emails to %{count} address(es)? The emails will be sent to the REAL recipients listed.')
            .replace('%{count}', emailAddresses.length),
        cb_confirm: async () => {
          try {
            const result = await this.sendToEmails({
              emailType: this.emailType,
              emailAddresses,
              sendOnlyPending,
              filters: this.getCurrentFilters()
            })

            this.sendResults = result

            if (result.error_count === 0) {
              await this.notifySuccess(
                  this.$pgettext('email_templates.debug_logs', 'Bulk emails sent successfully')
              )
            }

            this.$refs.ajaxTable.fetchList()
          } catch (error) {
            await this.notifyError(
                this.$pgettext('email_templates.debug_logs', 'Failed to send bulk emails')
            )
          }
        }
      })
    },

    async exportToExcel() {
      try {
        await this.exportDebugLogs({
          emailType: this.emailType,
          filters: this.getCurrentFilters()
        })
        await this.notifySuccess(
            this.$pgettext('email_templates.debug_logs', 'Debug logs exported successfully')
        )
      } catch (error) {
        await this.notifyError(
            this.$pgettext('email_templates.debug_logs', 'Failed to export debug logs')
        )
      }
    },

    async replaySelected() {
      if (this.selectedLogs.length === 0) {
        await this.notifyError(
            this.$pgettext('email_templates.debug_logs', 'Please select at least one email to send')
        )
        return
      }

      this.$refs.sendSelectedModal.open()
    },

    async handleSendSelected(recipientAddress) {
      try {
        const result = await this.replaySelectedDebugLogs({
          emailType: this.emailType,
          logIds: this.selectedLogs,
          recipientAddress
        })

        this.sendResults = result
        this.selectedLogs = []

        if (result.error_count === 0) {
          await this.notifySuccess(
              this.$pgettext('email_templates.debug_logs', 'Selected emails sent successfully')
          )
        }

        this.$refs.ajaxTable.fetchList()
      } catch (error) {
        await this.notifyError(
            this.$pgettext('email_templates.debug_logs', 'Failed to send selected emails')
        )
      }
    },

    async deleteSelected() {
      if (this.selectedLogs.length === 0) {
        await this.notifyError(
            this.$pgettext('email_templates.debug_logs', 'Please select at least one email to delete')
        )
        return
      }

      const message = this.$pgettext('email_templates.debug_logs', 'Are you sure you want to delete %{count} selected email(s)? This action is irreversible.')
          .replace('%{count}', this.selectedLogs.length)

      this.confirm({
        title: this.$pgettext('email_templates.debug_logs', 'Delete Selected Emails'),
        message,
        cb_confirm: async () => {
          try {
            const result = await this.deleteSelectedDebugLogs({
              emailType: this.emailType,
              logIds: this.selectedLogs
            })

            await this.notifySuccess(
                this.$pgettext('email_templates.debug_logs', 'Successfully deleted %{count} email(s)')
                    .replace('%{count}', result.deleted_count)
            )

            this.selectedLogs = []
            this.$refs.ajaxTable.fetchList()
          } catch (error) {
            await this.notifyError(
                this.$pgettext('email_templates.debug_logs', 'Failed to delete emails')
            )
          }
        }
      })
    },

    async markAsSent() {
      if (this.selectedLogs.length === 0) {
        await this.notifyError(
            this.$pgettext('email_templates.debug_logs', 'Please select at least one email to mark as sent')
        )
        return
      }

      const message = this.$pgettext('email_templates.debug_logs', 'Are you sure you want to mark %{count} email(s) as sent? This will update their status without actually sending them.')
          .replace('%{count}', this.selectedLogs.length)

      this.confirm({
        title: this.$pgettext('email_templates.debug_logs', 'Mark as Sent'),
        message,
        cb_confirm: async () => {
          try {
            const result = await this.markDebugLogsAsSent({
              emailType: this.emailType,
              logIds: this.selectedLogs
            })

            await this.notifySuccess(
                this.$pgettext('email_templates.debug_logs', 'Successfully marked %{count} email(s) as sent')
                    .replace('%{count}', result.updated_count)
            )

            this.selectedLogs = []
            this.$refs.ajaxTable.fetchList()
          } catch (error) {
            await this.notifyError(
                this.$pgettext('email_templates.debug_logs', 'Failed to mark emails as sent')
            )
          }
        }
      })
    },

    getStatusLabel(log) {
      if (log.sent_at === null) {
        return this.$pgettext('email_templates.debug_logs', 'Pending')
      }
      return log.sent_successfully
          ? this.$pgettext('email_templates.debug_logs', 'Sent')
          : this.$pgettext('email_templates.debug_logs', 'Failed')
    },

    getStatusColor(log) {
      if (log.sent_at === null) return 'secondary'
      return log.sent_successfully ? 'success' : 'danger'
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleString()
    }
  }
}
</script>

<style scoped>
.gap-2 {
  gap: 0.5rem;
}
</style>
